// ═══════════════════════════════════════════════════════════════
// PersonnelBlock.tsx – élő blokkok a bejegyzésekben
//   {{SZERVEZETI_DIAGRAM}} / {{SZERVEZETI_DIAGRAM:<egység>}}            → diagram (anyaszervezet)
//   {{KAPCSOLATOK}} / {{KAPCSOLATOK:<egység>}}                          → kapcsolattartók (anyaszervezet)
//   {{SZERVEZETI_DIAGRAM:intezmeny-<E>[:<egység>]}} / {{KAPCSOLATOK:intezmeny-<E>[:<egység>]}}
//                                                                       → egy kapcsolt intézmény belső szervezete
//   {{KAPCSOLT_SZERVEK[:<típus>]}}, {{KAPCSOLT_SZERV:<id>}}, {{KAPCSOLATI_TERKEP[:<id>]}}
//                                                                       → kapcsolt szervek, térkép
// Az adatot a környezet (PersonnelDataProvider) adja: a nyilvános oldalon a szerver által
// szűrt adat, a kezelőfelületen a szervezet adatai (lusta betöltés, csak hivatkozáskor).
// ═══════════════════════════════════════════════════════════════
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { LinkedEntity, LinkedEntityType, LinkedSettings, OrgChartSettings } from '../../types';
import { ApiClient } from '../../api/client';
import { OrgChart } from './OrgChart';
import { ORG_CHART_DEFAULTS, fromOverview, childUnits, descendantIds, positionPersons, unitLoosePersons, unitPositions, unitPath, type OrgModel } from './orgModel';
import { DEFAULT_LINKED_SETTINGS, ENTITY_TYPE_GROUP, ENTITY_TYPE_LABEL, entitiesToOrgModel, entityDescendants, entityDetailRows, relationLabel } from './linkedModel';
import { Phone, Mail, MapPin, Network, Contact, Building2, Share2 } from 'lucide-react';

const PERSONNEL_PART = String.raw`\{\{(SZERVEZETI_DIAGRAM|KAPCSOLATOK)(?::(?:intezmeny-(\d+)(?::(\d+))?|(\d+)))?\}\}`;
const LINKED_PART = String.raw`\{\{(KAPCSOLT_SZERVEK|KAPCSOLT_SZERV|KAPCSOLATI_TERKEP)(?::([a-z0-9_]+))?\}\}`;
const liveRe = (flags = 'g') => new RegExp(`${PERSONNEL_PART}|${LINKED_PART}`, flags);
/** minden élő hivatkozás (szervezeti és kapcsolt szerv) */
export const LIVE_TOKEN_RE = liveRe();
/** @deprecated a régi név */
export const PERSONNEL_TOKEN_RE = LIVE_TOKEN_RE;
export const hasLiveToken = (s?: string | null) => !!s && liveRe('').test(s);
export const hasPersonnelToken = hasLiveToken;

export type LiveToken =
  | { kind: 'SZERVEZETI_DIAGRAM' | 'KAPCSOLATOK'; entityId: number | null; unitId: number | null }
  | { kind: 'KAPCSOLT_SZERVEK' | 'KAPCSOLT_SZERV' | 'KAPCSOLATI_TERKEP'; arg: string | null };

function tokenFromMatch(m: RegExpMatchArray | string[]): LiveToken {
  if (m[1]) return { kind: m[1] as 'SZERVEZETI_DIAGRAM' | 'KAPCSOLATOK', entityId: m[2] ? Number(m[2]) : null, unitId: m[3] ? Number(m[3]) : (m[4] ? Number(m[4]) : null) };
  return { kind: m[5] as 'KAPCSOLT_SZERVEK' | 'KAPCSOLT_SZERV' | 'KAPCSOLATI_TERKEP', arg: m[6] || null };
}

const TOKEN_LABEL: Record<LiveToken['kind'], string> = {
  SZERVEZETI_DIAGRAM: 'Szervezeti diagram', KAPCSOLATOK: 'Kapcsolattartók',
  KAPCSOLT_SZERVEK: 'Kapcsolt szervek', KAPCSOLT_SZERV: 'Kapcsolt szerv adatlapja', KAPCSOLATI_TERKEP: 'Kapcsolati térkép',
};
/** a táblázatos összegzésben a hivatkozás helyett olvasható címke */
export const replaceLiveTokens = (s: string) => s.replace(liveRe(), (...m) => `[${TOKEN_LABEL[tokenFromMatch(m as string[]).kind]}]`);

export interface PersonnelData { model: OrgModel; settings: OrgChartSettings; rootTitle: string; brandColor?: string }
export interface LinkedData { entities: LinkedEntity[]; settings: Pick<LinkedSettings, 'relation_types' | 'map'>; rootTitle: string; brandColor?: string }
export interface LiveBundle { own?: PersonnelData | null; entities?: Record<number, PersonnelData> | null; linked?: LinkedData | null }
export interface LiveLoaders {
  personnel?: (entityId: number | null) => Promise<PersonnelData | null>;
  linked?: () => Promise<LinkedData | null>;
}

type Key = string; // 'own' | 'ent:<id>' | 'linked'
interface Ctx { get: (k: Key) => unknown; isLoading: (k: Key) => boolean; request: (k: Key) => void }
const PersonnelCtx = createContext<Ctx | null>(null);

/**
 * @param data    kész adat az anyaszervezethez (régi használat)
 * @param bundle  kész adat (nyilvános oldal): anyaszervezet, kapcsolt intézmények, kapcsolt szervek
 * @param loader  lusta betöltés az anyaszervezethez (régi használat)
 * @param loaders lusta betöltés (kezelőfelület) – csak akkor fut, ha egy bejegyzés hivatkozik rá
 */
export const PersonnelDataProvider: React.FC<{
  data?: PersonnelData | null; bundle?: LiveBundle | null; loader?: () => Promise<PersonnelData | null>; loaders?: LiveLoaders;
  reloadKey?: unknown; children: React.ReactNode;
}> = ({ data, bundle, loader, loaders, reloadKey, children }) => {
  const [cache, setCache] = useState<Record<Key, unknown>>({});
  const [loading, setLoading] = useState<Record<Key, boolean>>({});
  const pending = useRef<Set<Key>>(new Set());
  const src = useRef<{ loader?: typeof loader; loaders?: LiveLoaders }>({});
  src.current = { loader, loaders };
  useEffect(() => { setCache({}); setLoading({}); pending.current.clear(); }, [reloadKey]);

  const fromBundle = (k: Key): unknown => {
    if (k === 'own') return bundle?.own ?? data ?? undefined;
    if (k === 'linked') return bundle?.linked ?? undefined;
    if (k.startsWith('ent:')) return bundle?.entities?.[Number(k.slice(4))] ?? undefined;
    return undefined;
  };
  const request = useCallback((k: Key) => {
    if (pending.current.has(k)) return;
    const { loader: l1, loaders: ls } = src.current;
    let job: (() => Promise<unknown>) | null = null;
    if (k === 'own') job = ls?.personnel ? () => ls.personnel!(null) : (l1 || null);
    else if (k === 'linked') job = ls?.linked || null;
    else if (k.startsWith('ent:') && ls?.personnel) job = () => ls.personnel!(Number(k.slice(4)));
    if (!job) return;
    pending.current.add(k);
    setLoading((s) => ({ ...s, [k]: true }));
    job().then((d) => setCache((c) => ({ ...c, [k]: d ?? null })))
      .catch(() => setCache((c) => ({ ...c, [k]: null })))
      .finally(() => setLoading((s) => ({ ...s, [k]: false })));
  }, []);
  const value: Ctx = { get: (k) => fromBundle(k) ?? cache[k] ?? null, isLoading: (k) => !!loading[k], request };
  return <PersonnelCtx.Provider value={value}>{children}</PersonnelCtx.Provider>;
};

/** kezelőfelület: a szervezet (vagy egy kapcsolt intézmény) személyzeti adata a diagramhoz / kapcsolattartókhoz */
export async function loadAdminPersonnelData(tenantId?: number, tenantName?: string, entityId?: number | null, entityName?: string): Promise<PersonnelData | null> {
  const res = await ApiClient.getPersonnelOverview(tenantId, entityId);
  if (!res.ok || !res.data) return null;
  const d = res.data;
  const settings = { ...ORG_CHART_DEFAULTS, ...(d.chart_settings || {}) };
  let name = entityId ? entityName : tenantName;
  if (entityId && !name) {
    const l = await ApiClient.getLinkedEntities({ tenant_id: tenantId });
    name = l.ok ? l.data?.find((e) => e.id === entityId)?.name : '';
  }
  return {
    model: fromOverview(d.org_units || [], d.positions || [], d.persons || [], d.current_assignments || []),
    settings,
    rootTitle: settings.root_title || name || '',
  };
}

/** kezelőfelület: kapcsolt szervek a blokkokhoz – a nyilvános oldallal azonos mezőkkel */
export async function loadAdminLinkedData(tenantId?: number, tenantName?: string): Promise<LinkedData | null> {
  const [l, s] = await Promise.all([ApiClient.getLinkedEntities({ tenant_id: tenantId }), ApiClient.getLinkedSettings(tenantId)]);
  if (!l.ok || !l.data) return null;
  const settings = { ...DEFAULT_LINKED_SETTINGS, ...(s.ok && s.data ? s.data : {}) };
  return {
    entities: l.data.map((e) => ({ ...e, notes: null, bank_account: e.bank_account_public ? e.bank_account : null })),
    settings: { relation_types: settings.relation_types, map: settings.map },
    rootTitle: settings.map.root_title || tenantName || '',
  };
}

const ContactsBlock: React.FC<{ data: PersonnelData; unitId: number | null }> = ({ data, unitId }) => {
  const m = data.model;
  const ids = unitId ? descendantIds(m, unitId) : new Set(m.units.map((u) => u.id));
  const ordered: number[] = [];
  const walk = (pid: number | null, guard = 0) => {
    if (guard > 15) return;
    for (const u of childUnits(m, pid)) { if (ids.has(u.id)) ordered.push(u.id); walk(u.id, guard + 1); }
  };
  if (unitId) { ordered.push(unitId); walk(unitId); } else walk(null);
  const units = ordered.map((id) => m.units.find((u) => u.id === id)!).filter(Boolean);
  if (!units.length) return <p className="text-xs text-slate-400">(nincs megjeleníthető kapcsolattartó)</p>;
  return (
    <div className="not-prose my-2 space-y-3">
      {units.map((u) => {
        const rows: { name: string; role: string; phone?: string | null; email?: string | null; room?: string | null; leader: boolean }[] = [];
        for (const pos of unitPositions(m, u.id)) for (const p of positionPersons(m, pos.id)) rows.push({ name: p.name, role: p.title || pos.title, phone: p.phone, email: p.email, room: p.room, leader: !!pos.is_leader });
        for (const p of unitLoosePersons(m, u.id)) rows.push({ name: p.name, role: p.title || '', phone: p.phone, email: p.email, room: p.room, leader: false });
        return (
          <div key={u.id} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 text-sm font-semibold text-slate-900 dark:text-white flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{unitPath(m, u.id)}</span>
              {u.phone && <a href={`tel:${u.phone}`} className="text-xs font-normal text-slate-600 dark:text-slate-300 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{u.phone}</a>}
              {u.email && <a href={`mailto:${u.email}`} className="text-xs font-normal text-indigo-600 dark:text-indigo-400 inline-flex items-center gap-1"><Mail className="w-3 h-3" />{u.email}</a>}
              {u.address && <span className="text-xs font-normal text-slate-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{u.address}</span>}
            </div>
            {rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-1.5 font-medium text-slate-900 dark:text-slate-100">{r.name}{r.leader && <span className="ml-1 text-[10px] text-amber-700 dark:text-amber-300">(vezető)</span>}</td>
                        <td className="px-3 py-1.5 text-slate-600 dark:text-slate-400">{r.role}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap">{r.phone && <a href={`tel:${r.phone}`} className="text-slate-700 dark:text-slate-300">{r.phone}</a>}</td>
                        <td className="px-3 py-1.5">{r.email && <a href={`mailto:${r.email}`} className="text-indigo-600 dark:text-indigo-400 break-all">{r.email}</a>}</td>
                        <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{r.room ? `Szoba: ${r.room}` : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const relText = (settings: LinkedData['settings'], e: LinkedEntity) =>
  [relationLabel(settings, e.relation_type), e.share_percent !== undefined && e.share_percent !== null && e.share_percent !== '' ? `${e.share_percent}%` : ''].filter(Boolean).join(' · ');

/** egy kapcsolt szerv adatlapja */
export const EntityCard: React.FC<{ e: LinkedEntity; settings: LinkedData['settings']; showType?: boolean }> = ({ e, settings, showType = true }) => {
  const rows = entityDetailRows(e);
  const rel = relText(settings, e);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
      <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 flex flex-wrap items-center gap-x-2 gap-y-1">
        <Building2 className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
        <span className="text-sm font-semibold text-slate-900 dark:text-white">{e.name}</span>
        {showType && <span className="text-[10px] px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300">{ENTITY_TYPE_LABEL[e.entity_type as LinkedEntityType] || e.entity_type}</span>}
        {rel && <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 dark:bg-violet-950/40 border border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300">kapcsolat: {rel}</span>}
      </div>
      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((r, i) => (
                <tr key={i}>
                  <th scope="row" className="px-3 py-1.5 text-left font-medium text-slate-500 dark:text-slate-400 w-48 align-top">{r.label}</th>
                  <td className="px-3 py-1.5 text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-words">
                    {r.href ? <a href={r.href} className="text-indigo-600 dark:text-indigo-400 break-all" rel="noopener noreferrer" target={r.href.startsWith('http') ? '_blank' : undefined}>{r.value}</a> : r.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** a kapcsolati térkép (megjelenítés; szerkesztéshez edit-tel) */
export const LinkedMapView: React.FC<{ data: LinkedData; rootId?: number | null; edit?: React.ComponentProps<typeof OrgChart>['edit'] }> = ({ data, rootId, edit }) => {
  const known = new Set(data.entities.map((e) => e.id));
  const ents = data.entities.map((e) => ({ ...e, parent_entity_id: e.parent_entity_id && known.has(e.parent_entity_id) ? e.parent_entity_id : null }));
  const scope = rootId ? entityDescendants(ents, rootId) : null;
  const model = entitiesToOrgModel(scope ? ents.filter((e) => scope.has(e.id)) : ents);
  const byId = new Map(data.entities.map((e) => [e.id, e]));
  return (
    <OrgChart
      model={model}
      settings={{ orientation: data.settings.map?.orientation || 'vertical', detail: 'units', root_title: data.rootTitle }}
      rootTitle={data.rootTitle}
      rootUnitId={rootId || null}
      brandColor={data.brandColor}
      edit={edit}
      labels={{ addChild: 'Hozzá kapcsolódó szerv felvétele', addTop: 'Kapcsolt szerv felvétele', staffTag: 'oldalági kapcsolat', toBeside: 'Mellé (oldalági kapcsolat)', toBelow: 'Alá (alárendelt kapcsolat)', emptyRoot: 'A szervezet' }}
      renderExtra={(u) => {
        const e = byId.get(u.id);
        if (!e) return null;
        const rel = relText(data.settings, e);
        return (
          <div className="oc-people">
            <div className="oc-person opacity-80">{ENTITY_TYPE_LABEL[e.entity_type as LinkedEntityType] || e.entity_type}</div>
            {rel && <div className="oc-person oc-leader"><Share2 className="w-3 h-3 shrink-0" /> {rel}</div>}
            {e.leader_name && <div className="oc-person"><span className="opacity-70">{e.leader_title || 'Vezető'}:</span> {e.leader_name}</div>}
          </div>
        );
      }}
    />
  );
};

const Placeholder: React.FC<{ label: string; icon: React.FC<{ className?: string }>; loading: boolean }> = ({ label, icon: Icon, loading }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 text-xs border border-indigo-200 dark:border-indigo-800">
    <Icon className="w-3 h-3" /> {label}{loading ? ' (betöltés…)' : ''}
  </span>
);

/** egy élő hivatkozás megjelenítése */
export const LiveBlock: React.FC<{ token: LiveToken }> = ({ token }) => {
  const ctx = useContext(PersonnelCtx);
  const isPersonnel = token.kind === 'SZERVEZETI_DIAGRAM' || token.kind === 'KAPCSOLATOK';
  const entityId = isPersonnel ? (token as { entityId: number | null }).entityId : null;
  const key: Key = isPersonnel ? (entityId ? `ent:${entityId}` : 'own') : 'linked';
  const loaded = ctx?.get(key);
  useEffect(() => { if (ctx && !loaded) ctx.request(key); }, [ctx, key, loaded]);
  const label = TOKEN_LABEL[token.kind];
  const Icon = token.kind === 'KAPCSOLATOK' ? Contact : token.kind === 'KAPCSOLT_SZERVEK' || token.kind === 'KAPCSOLT_SZERV' ? Building2 : Network;
  if (!ctx || !loaded) return <Placeholder label={label} icon={Icon} loading={!!ctx?.isLoading(key)} />;

  if (isPersonnel) {
    const d = loaded as PersonnelData;
    const unitId = (token as { unitId: number | null }).unitId;
    if (unitId && !d.model.units.some((u) => u.id === unitId)) return <span className="text-xs text-slate-400">({label}: a hivatkozott egység már nem létezik)</span>;
    if (token.kind === 'KAPCSOLATOK') return <ContactsBlock data={d} unitId={unitId} />;
    return (
      <div className="not-prose my-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40">
        <OrgChart model={d.model} settings={d.settings || ORG_CHART_DEFAULTS} rootTitle={d.rootTitle} rootUnitId={unitId} brandColor={d.brandColor} />
      </div>
    );
  }

  const d = loaded as LinkedData;
  const arg = (token as { arg: string | null }).arg;
  if (token.kind === 'KAPCSOLT_SZERV') {
    const e = d.entities.find((x) => x.id === Number(arg));
    return e ? <div className="not-prose my-2"><EntityCard e={e} settings={d.settings} /></div> : <span className="text-xs text-slate-400">({label}: a hivatkozott szerv már nem létezik)</span>;
  }
  if (token.kind === 'KAPCSOLT_SZERVEK') {
    const list = d.entities.filter((e) => !arg || e.entity_type === arg);
    if (!list.length) return <p className="text-xs text-slate-400">(nincs megjeleníthető kapcsolt szerv)</p>;
    const groups = new Map<string, LinkedEntity[]>();
    for (const e of list) { const g = groups.get(e.entity_type) || []; g.push(e); groups.set(e.entity_type, g); }
    return (
      <div className="not-prose my-2 space-y-3">
        {[...groups.entries()].map(([t, es]) => (
          <div key={t} className="space-y-2">
            {!arg && <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">{ENTITY_TYPE_GROUP[t as LinkedEntityType] || t}</div>}
            {es.map((e) => <EntityCard key={e.id} e={e} settings={d.settings} showType={false} />)}
          </div>
        ))}
      </div>
    );
  }
  const rootId = arg ? Number(arg) : null;
  if (rootId && !d.entities.some((e) => e.id === rootId)) return <span className="text-xs text-slate-400">({label}: a hivatkozott szerv már nem létezik)</span>;
  return (
    <div className="not-prose my-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-900/40">
      <LinkedMapView data={d} rootId={rootId} />
    </div>
  );
};

/** régi használat: szervezeti blokk */
export const PersonnelBlock: React.FC<{ kind: 'SZERVEZETI_DIAGRAM' | 'KAPCSOLATOK'; unitId: number | null; entityId?: number | null }> = ({ kind, unitId, entityId = null }) =>
  <LiveBlock token={{ kind, unitId, entityId }} />;

export type LivePart = { type: 'text'; value: string } | { type: 'token'; token: LiveToken };

/** szöveg/HTML feldarabolása az élő hivatkozások mentén */
export function splitPersonnelTokens(src: string): LivePart[] {
  const out: LivePart[] = [];
  let last = 0;
  // a csak hivatkozást tartalmazó bekezdés helyére maga a blokk kerül
  const s = src.replace(new RegExp(String.raw`<p>\s*((?:${PERSONNEL_PART})|(?:${LINKED_PART}))\s*</p>`, 'g'), (_m, tok) => tok);
  for (const mm of s.matchAll(liveRe())) {
    if (mm.index! > last) out.push({ type: 'text', value: s.slice(last, mm.index) });
    out.push({ type: 'token', token: tokenFromMatch(mm) });
    last = mm.index! + mm[0].length;
  }
  if (last < s.length) out.push({ type: 'text', value: s.slice(last) });
  return out;
}
export const splitLiveTokens = splitPersonnelTokens;
